import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { BulkActionResponseData } from '@utils/types/back-office/priority-queue';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type BulkRequeueStuckParams = {
  max_items?: number;
  older_than_minutes?: number;
  reset_retry_count?: boolean;
};

export const useBulkRequeueStuck = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const bulkRequeueStuck = async (params?: BulkRequeueStuckParams) => {
    const payload = {
      max_items: params?.max_items ?? 100,
      older_than_minutes: params?.older_than_minutes ?? 30,
      reset_retry_count: params?.reset_retry_count ?? false
    };
    const response = await Axios.post<BulkActionResponseData>(
      `${POST_URL().bulkRequeueStuck}`,
      payload
    );
    return response.data;
  };

  return useMutation<
    BulkActionResponseData,
    AxiosError<BaseError>,
    BulkRequeueStuckParams | undefined
  >(bulkRequeueStuck, {
    onSuccess: data => {
      const count = data.result?.affected_count || 0;
      toast.success(`${t('requeued-stuck-items-successfully')}: ${count}`);
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-requeue-stuck-items');
      toast.error(errorMessage);
    }
  });
};
